import './CreateSubscription.less';
import { Card, Col, Row, Tabs } from 'antd';
import React, { useState } from 'react';
import useCreateAndProcessSubscription from '../hooks/useCreateAndProcessSubscription';
import { Token } from '../types';
import { send3DSecureErrorEvent, send3DSecureSuccessEvent } from '../../utils/analytics';
import CreditCardPaymentMethod from '../payment/CreditCardPaymentMethod';
import PayPalPaymentMethod from '../payment/PayPalPaymentMethod';
import PaymentMethodPromo from '../payment/PaymentMethodPromo';
import SelectedPlanDetails from './SelectedPlanDetails';

type Props = {
    className?: string;
    title: string;
    buttonLabel: string;
    selectedPlanCode: string | undefined;
    redirectTo?: string;
    isInFreeTrialSetup?: boolean;
    onPlanCodeChange?: (planCode: string) => void;
};

export default function CreateSubscription({
    className,
    title,
    buttonLabel,
    selectedPlanCode,
    redirectTo,
    isInFreeTrialSetup,
    onPlanCodeChange = () => { },
}: Props) {
    const [selectedTab, setSelectedTab] = useState<string>('credit_card');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { createAndProcessSubscription, removePendingTokens, pendingPaymentTokens, isCreatingSubscription } = useCreateAndProcessSubscription();

    const handleToken = async (token: Token) => {
        if (!selectedPlanCode) {
            setErrorMessage('Something went wrong. Please try again');
            return;
        }

        setErrorMessage('');

        const result = await createAndProcessSubscription(token.id, token.type, selectedPlanCode, redirectTo);
        if (result?.error) {
            setErrorMessage(result.error);
        }
    };

    const handle3DSecureResultToken = async (token: Token) => {
        if (!selectedPlanCode) {
            setErrorMessage('Something went wrong. Please try again');
            return;
        }

        setErrorMessage('');

        const result = await createAndProcessSubscription(pendingPaymentTokens.creditCardToken, 'credit_card', selectedPlanCode, redirectTo, token.id);

        if (result?.error) {
            setErrorMessage(result.error);
        } else {
            send3DSecureSuccessEvent();
        }
    };

    const handle3DSecureError = (error: any) => {
        const message = error.message || 'We were unable to authenticate your payment method… choose a different payment method and try again.';
        setErrorMessage(message);

        let userEmail = 'unknown';

        try {
            userEmail = error?.cause?.payment_method?.billing_details?.email;
        } catch (error) { }

        send3DSecureErrorEvent(userEmail, error.name, error.code);
        removePendingTokens();
    };

    const handleError = (error: string) => {
        setErrorMessage(error);
    };

    return (
        <div className={className}>
            <Card
                title={title}
                className="payment-card"
                bordered={false}
            >
                <Row
                    gutter={24}
                    className="main-row"
                >
                    <Col
                        span={12}
                        className="main-column"
                    >
                        <Tabs
                            className="payment-tabs"
                            activeKey={selectedTab}
                            onChange={setSelectedTab}
                            type="card"
                        >
                            <Tabs.TabPane
                                tab="Credit card"
                                key="credit_card"
                            >
                                <CreditCardPaymentMethod
                                    attemptType="checkout"
                                    buttonLabel={buttonLabel}
                                    showAvailableCards
                                    threeDSecureActionToken={pendingPaymentTokens.threeDSecureActionToken}
                                    isLoading={isCreatingSubscription}
                                    errorMessage={errorMessage}
                                    onToken={handleToken}
                                    on3DSecureResultToken={handle3DSecureResultToken}
                                    on3DSecureError={handle3DSecureError}
                                    onError={handleError}
                                />
                            </Tabs.TabPane>

                            <Tabs.TabPane
                                tab="PayPal"
                                key="paypal"
                            >
                                <SelectedPlanDetails
                                    selectedPlanCode={selectedPlanCode}
                                    isInFreeTrialSetup={isInFreeTrialSetup}
                                    onSelectPlan={onPlanCodeChange}
                                />

                                <PayPalPaymentMethod
                                    buttonLabel={buttonLabel}
                                    onToken={handleToken}
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    </Col>

                    <Col
                        span={12}
                        className="main-column"
                    >
                        {selectedTab === 'credit_card' && (
                            <SelectedPlanDetails
                                selectedPlanCode={selectedPlanCode}
                                isInFreeTrialSetup={isInFreeTrialSetup}
                                onSelectPlan={onPlanCodeChange}
                            />
                        )}

                        <PaymentMethodPromo
                            paymentMethod={selectedTab}
                            includeRefundDetails={isInFreeTrialSetup}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
}
